import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import SheinSyncRangeModal from '@/shein-platform/components/SheinSyncRangeModal';
import SheinDateRangeFilter, {
  SHEIN_RETURN_TIME_TYPE_OPTIONS,
  pickDatePart,
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/shein-platform/components/SheinDateRangeFilter';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { RETURN_ORDER_STATUS_LABELS } from '@/shein-platform/constants/sheinApiCatalog';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, extractSheinTotal, pickField } from '@/shein-platform/utils/sheinResponse';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import {
  extractGoodsIdList,
  listProductSummary,
} from '@/shein-platform/modules/aftersales/returnOrderDisplay';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import { readNoticeReturnOrderNo } from '@/shein-platform/utils/sheinNoticeFocusQuery';
import '@/styles/shein-platform/shein-product-ops.css';

const RETURN_STATUS_KEYS = {
  '待处理': 'rsPending',
  '已拒绝': 'rsRejected',
  '待买家寄回': 'rsAwaitBuyer',
  '买家已寄出': 'rsBuyerShipped',
  '拒绝收货': 'rsRejectReceive',
  '退货成功': 'rsReturnSuccess',
  '已取消': 'rsCancelled',
  '已完成': 'rsCompleted',
  '已关闭': 'rsClosed',
  '申请中': 'rsApplying',
  '卖家已收货': 'rsSellerReceived',
  '已送达': 'rsDelivered',
  '待交接': 'rsAwaitHandover',
  '待TEMU转账': 'rsTemuTransfer',
  '待SHEIN转账': 'rsSheinTransfer',
};

function returnStatusText(code) {
  const raw = RETURN_ORDER_STATUS_LABELS[code];
  const key = raw ? RETURN_STATUS_KEYS[raw] : '';
  if (key) return i18n.t(`returnList.${key}`, { ns: 'workbench' });
  if (raw) return raw;
  return code == null || code === '' ? '—' : String(code);
}

const STATUS_OPTIONS = Object.entries(RETURN_ORDER_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const PRODUCT_ID_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'sku', label: 'SKU' },
];

const ORDER_ID_OPTIONS = [
  { value: 'returnOrderNo', labelKey: 'returnList.returnNo' },
  { value: 'orderNo', labelKey: 'returnList.relatedOrder' },
];

const EMPTY_FILTER = {
  orderIdType: 'returnOrderNo',
  orderIdValue: '',
  returnReason: '',
  productIdType: 'skc',
  productIdValue: '',
  timeType: 'apply',
  startTime: '',
  endTime: '',
  status: '',
};

function statusModifier(code) {
  const label = RETURN_ORDER_STATUS_LABELS[code] || String(code || '');
  if (/完成|成功|已签收|已退款/.test(label)) return 'success';
  if (/拒绝|取消|失败|关闭/.test(label)) return 'failed';
  if (/待|处理中|审核|运输/.test(label)) return 'pending';
  return 'pending';
}

function rowKey(row, index) {
  return String(pickField(row, 'returnOrderNo', 'returnNo') || `row-${index}`);
}

export default function ReturnOrderListPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    activeShopId: topActiveShopId,
    loading: shopLoading,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const activeShopId = concreteScopeId || (allShopsScope ? '' : topActiveShopId);

  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [query, setQuery] = useState(() => ({
    ...EMPTY_FILTER,
    _nonce: 0,
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [signingNo, setSigningNo] = useState('');
  const [batchSigning, setBatchSigning] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const pendingSyncRangeRef = useRef(null);

  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setSelectedKeys(new Set());
  }, [activeShopId]);

  const noticeReturnOrderNo = readNoticeReturnOrderNo(searchParams);
  useEffect(() => {
    if (!noticeReturnOrderNo) return;
    const next = {
      ...EMPTY_FILTER,
      orderIdType: 'returnOrderNo',
      orderIdValue: noticeReturnOrderNo,
    };
    setFilterDraft(next);
    setQuery((prev) => ({ ...next, _nonce: (prev._nonce || 0) + 1 }));
    setPage(1);
  }, [noticeReturnOrderNo, activeShopId]);

  useEffect(() => {
    // 仅筛选条件变化时清空选中；翻页保留跨页勾选
    setSelectedKeys(new Set());
  }, [query._nonce]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'RETURN');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const queryParams = useMemo(() => {
    const statusNum = query.status === '' || query.status == null ? null : Number(query.status);
    const idType = query.productIdType === 'sku' ? 'sku' : 'skc';
    const idValue = query.productIdValue || undefined;
    const orderType = query.orderIdType === 'orderNo' ? 'orderNo' : 'returnOrderNo';
    const orderValue = query.orderIdValue || undefined;
    const startDate = pickDatePart(query.startTime);
    const endDate = pickDatePart(query.endTime);
    return {
      page,
      pageSize,
      source: 'cached',
      returnOrderStatus: Number.isFinite(statusNum) ? statusNum : undefined,
      returnOrderNo: orderType === 'returnOrderNo' ? orderValue : undefined,
      orderNo: orderType === 'orderNo' ? orderValue : undefined,
      returnReason: query.returnReason || undefined,
      skc: idType === 'skc' ? idValue : undefined,
      sku: idType === 'sku' ? idValue : undefined,
      timeType: query.timeType || 'apply',
      startTime: toDayStartDateTime(startDate) || undefined,
      endTime: toDayEndDateTime(endDate) || undefined,
      _nonce: query._nonce,
    };
  }, [query, page, pageSize]);

  const load = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { _nonce, ...params } = queryParams;
      const res = await sheinPlatformAPI.listReturnOrders(activeShopId, params);
      const list = extractSheinList(res);
      setRows(list);
      setTotal(extractSheinTotal(res, list.length));
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('returnList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, queryParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) return;
    try {
      const track = await startJob({
        kind: 'returnOrders',
        shopId: activeShopId,
        label: t('returnList.syncUpdate'),
        payload: {
          queryType: 2,
          startTime: range.startTime,
          endTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
      setSyncRangeOpen(false);
      pendingSyncRangeRef.current = null;
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('returnList.syncFailed'), 'error');
    }
  }, [activeShopId, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('returnOrders', activeShopId);

  const resolveDetailForSign = async (returnOrderNo) => {
    try {
      const cached = await sheinPlatformAPI.getCachedReturnOrderDetail(activeShopId, returnOrderNo);
      const list = extractSheinList(cached);
      const detail = list[0] || cached;
      if (extractGoodsIdList(detail).length) return detail;
    } catch {
      /* fall through */
    }
    const live = await sheinPlatformAPI.getReturnOrderDetails(activeShopId, {
      returnOrderNoList: [returnOrderNo],
    });
    const list = extractSheinList(live);
    return list.find((row) => String(pickField(row, 'returnOrderNo', 'returnNo')) === String(returnOrderNo))
      || list[0]
      || live;
  };

  const handleSign = async (returnOrderNo) => {
    if (!activeShopId || !returnOrderNo) return;
    const ok = await showSheinConfirm({
      title: t('returnList.confirmSign'),
      message: t('returnList.signOne', { no: returnOrderNo, platform: 'SHEIN' }),
      tone: 'warning',
      confirmText: t('returnList.confirmSign'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSigningNo(returnOrderNo);
    try {
      const detail = await resolveDetailForSign(returnOrderNo);
      const goodsIdList = extractGoodsIdList(detail);
      if (!goodsIdList.length) {
        throw new Error(t('returnList.missingGoodsId'));
      }
      await sheinPlatformAPI.signReturnOrder(activeShopId, { returnOrderNo, goodsIdList });
      showToast(t('returnList.signSubmitted'), 'success');
      load();
    } catch (e) {
      showToast(e?.message || t('returnList.signFailed'), 'error');
    } finally {
      setSigningNo('');
    }
  };

  const openDetail = (returnOrderNo) => {
    if (!returnOrderNo) return;
    navigate(buildSheinPath(`orders/returns/${encodeURIComponent(returnOrderNo)}`));
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const orderTokens = parseBatchTokens(filterDraft.orderIdValue).slice(0, BATCH_QUERY_LIMIT);
    const productTokens = parseBatchTokens(filterDraft.productIdValue).slice(0, BATCH_QUERY_LIMIT);
    const start = pickDatePart(filterDraft.startTime);
    const end = pickDatePart(filterDraft.endTime);
    const rangeError = validateDateRange(start, end);
    if (rangeError) {
      showToast(rangeError, 'error');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setQuery((prev) => ({
      orderIdType: filterDraft.orderIdType === 'orderNo' ? 'orderNo' : 'returnOrderNo',
      orderIdValue: orderTokens.join(','),
      returnReason: String(filterDraft.returnReason || '').trim(),
      productIdType: filterDraft.productIdType === 'sku' ? 'sku' : 'skc',
      productIdValue: productTokens.join(','),
      timeType: filterDraft.timeType || 'apply',
      startTime: start,
      endTime: end,
      status: filterDraft.status || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    const nextDraft = { ...EMPTY_FILTER };
    setFilterDraft(nextDraft);
    setPage(1);
    setQuery((prev) => ({
      ...nextDraft,
      _nonce: prev._nonce + 1,
    }));
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('returnList.lastUpdate', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('returnList.notSynced');

  const emptyTitle = !activeShopId
    ? (allShopsScope ? t('returnList.pickConcrete') : t('returnList.pickShop'))
    : loading
      ? t('returnList.loading')
      : t('returnList.empty');

  const emptyDesc = !activeShopId || loading
    ? null
    : t('returnList.emptyPull', { platform: 'SHEIN' });

  const orderIdLabel = t(ORDER_ID_OPTIONS.find((o) => o.value === filterDraft.orderIdType)?.labelKey || 'returnList.returnNo');
  const productIdLabel = PRODUCT_ID_OPTIONS.find((o) => o.value === filterDraft.productIdType)?.label || 'SKC';
  const batchModalTitle = batchModalField === 'productIdValue'
    ? t('returnList.batchQueryType', { type: productIdLabel })
    : batchModalField === 'orderIdValue'
      ? t('returnList.batchQueryType', { type: orderIdLabel })
      : t('returnList.batchQuery');

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(() => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys]);


  const buildExportRows = (list) => {
    const headers = [
      'returnOrderNo',
      'orderNo',
      'returnReason',
      'returnOrderStatus',
      'statusLabel',
      'requestReturnTime',
      'sellerSignedTime',
      'completedTime',
      'goodsTitle',
      'skc',
      'skuSn',
      'refundAmount',
    ];
    const csvRows = (Array.isArray(list) ? list : []).map((row) => {
      const statusCode = pickField(row, 'returnOrderStatus', 'status');
      return {
        returnOrderNo: pickField(row, 'returnOrderNo', 'returnNo'),
        orderNo: pickField(row, 'orderNo', 'billNo', 'orderNumber'),
        returnReason: pickField(row, 'returnReason'),
        returnOrderStatus: statusCode,
        statusLabel: RETURN_ORDER_STATUS_LABELS[statusCode] || statusCode || '',
        requestReturnTime: pickField(row, 'requestReturnTime', 'applyTime'),
        sellerSignedTime: pickField(row, 'sellerSignedTime'),
        completedTime: pickField(row, 'completedTime'),
        goodsTitle: pickField(row, 'goodsTitle'),
        skc: pickField(row, 'skc'),
        skuSn: pickField(row, 'skuSn'),
        refundAmount: pickField(row, 'refundAmount'),
      };
    });
    return { headers, csvRows };
  };

  const exportRows = (list, emptyMessage) => {
    if (!list.length) {
      showToast(emptyMessage, 'warning');
      return;
    }
    const { headers, csvRows } = buildExportRows(list);
    downloadCsv(`shein-return-orders-${Date.now()}.csv`, headers, csvRows);
    showToast(t('returnList.exported', { count: csvRows.length }), 'success');
  };

  const exportSelected = () => {
    exportRows(selectedRows, t('returnList.pickToExport'));
  };

  const exportPage = () => {
    exportRows(rows, t('returnList.noneToExport'));
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('returnList.noneToExport'), 'warning');
      return;
    }
    setExporting(true);
    try {
      const { _nonce, ...baseParams } = queryParams;
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listReturnOrders(activeShopId, {
          ...baseParams,
          page: pageNo,
          pageSize: size,
        });
        const list = extractSheinList(res);
        return { items: list, total: extractSheinTotal(res, list.length) };
      });
      exportRows(all, t('returnList.noneToExport'));
    } catch (e) {
      showToast(e?.message || t('returnList.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const batchSignSelected = async () => {
    if (!activeShopId) return;
    if (!selectedRows.length) {
      showToast(t('returnList.pickToSign'), 'warning');
      return;
    }
    const returnOrderNoList = selectedRows
      .map((row) => pickField(row, 'returnOrderNo', 'returnNo'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    if (!returnOrderNoList.length) {
      showToast(t('returnList.missingReturnNo'), 'warning');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchConfirmSign'),
      message: t('returnList.batchSignMsg', { count: returnOrderNoList.length, platform: 'SHEIN' }),
      tone: 'warning',
      confirmText: t('returnList.confirmSign'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchSigning(true);
    try {
      // 由后端同店串行 + RateLimiter 调平台，避免浏览器 for 循环打满 sign≈40/s
      const summary = await sheinPlatformAPI.signReturnOrdersBatch(activeShopId, { returnOrderNoList });
      const successCount = Number(summary?.success ?? 0);
      const fail = Number(summary?.failed ?? 0);
      if (successCount > 0) {
        showToast(fail ? t('returnList.batchSignPartial', { ok: successCount, fail }) : t('returnList.batchSignDone', { count: successCount }), fail ? 'warning' : 'success');
        setSelectedKeys(new Set());
        load();
      } else {
        const tip = Array.isArray(summary?.errors) && summary.errors.length
          ? `：${summary.errors.slice(0, 2).join('；')}`
          : (fail ? t('returnList.failCount', { count: fail }) : '');
        showToast(`${t('returnList.batchSignFailed')}${tip}`, 'error');
      }
    } catch (e) {
      showToast(e?.message || t('returnList.batchSignFailed'), 'error');
    } finally {
      setBatchSigning(false);
    }
  };

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('returnList.pickToDelete'), 'warning');
      return;
    }
    const returnOrderNos = selectedRows
      .map((row) => pickField(row, 'returnOrderNo', 'returnNo'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (returnOrderNos.length === 0 && ids.length === 0) {
      showToast(t('returnList.missingReturnNoDelete'), 'error');
      return;
    }
    const countHint = returnOrderNos.length || ids.length;
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('returnList.batchDeleteMsg', { count: countHint, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteReturnOrders(activeShopId, {
        returnOrderNos,
        ids,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : countHint;
      if (count > 0) {
        showToast(t('returnList.deletedCount', { count }), 'success');
      } else {
        showToast(t('returnList.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      load();
    } catch (e) {
      showToast(e?.message || t('returnList.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={['returnOrders']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || batchSigning || exporting || deleting}
            syncKinds={['returnOrders']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('returnList.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('returnList.syncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
            extraActions={[
              {
                key: 'batch-sign',
                label: batchSigning ? t('returnList.signing') : t('returnList.batchConfirmSign'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || batchSigning,
                onClick: batchSignSelected,
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
              <select
                className="spo-select spo-select--compact spo-select--order-type"
                value={filterDraft.orderIdType}
                disabled={!activeShopId}
                aria-label={t('returnList.idTypeAria')}
                onChange={(e) => updateField('orderIdType', e.target.value)}
              >
                {ORDER_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.orderIdValue}
                placeholder={t('returnList.idPlaceholder', { type: orderIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('orderIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('returnList.batchInputId', { type: orderIdLabel })}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => setBatchModalField('orderIdValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
              <select
                className="spo-select spo-select--compact"
                value={filterDraft.productIdType}
                disabled={!activeShopId}
                aria-label={t('returnList.productIdAria')}
                onChange={(e) => updateField('productIdType', e.target.value)}
              >
                {PRODUCT_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.productIdValue}
                placeholder={t('returnList.idPlaceholder', { type: productIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('productIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('returnList.batchInputId', { type: productIdLabel })}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => setBatchModalField('productIdValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="spo-field spo-field--inline spo-field--status">
              <span className="spo-field__label">{t('returnList.status')}</span>
              <select
                className="spo-select spo-select--status"
                value={filterDraft.status}
                disabled={!activeShopId}
                aria-label={t('returnList.statusAria')}
                onChange={(e) => updateField('status', e.target.value)}
              >
                <option value="">{t('returnList.allStatus')}</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
            </div>
            <div className="spo-field spo-field--inline spo-field--w300">
              <span className="spo-field__label">{t('returnList.reason')}</span>
              <input
                className="spo-input"
                value={filterDraft.returnReason}
                placeholder={t('returnList.reasonPh')}
                disabled={!activeShopId}
                onChange={(e) => updateField('returnReason', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <SheinDateRangeFilter
              timeType={filterDraft.timeType}
              startDate={filterDraft.startTime}
              endDate={filterDraft.endTime}
              timeTypeOptions={SHEIN_RETURN_TIME_TYPE_OPTIONS}
              disabled={!activeShopId}
              onTimeTypeChange={(v) => updateField('timeType', v)}
              onStartDateChange={(v) => updateField('startTime', v)}
              onEndDateChange={(v) => updateField('endTime', v)}
            />
          </div>

          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              onClick={onReset}
              disabled={!activeShopId}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              onClick={onQuery}
              disabled={!activeShopId || loading}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? (
          <div className="spo-ops-error" role="alert">{error}</div>
        ) : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={t('returnList.batchLinePh')}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          updateField(batchModalField, joinBatchTokens(tokens));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <SheinSyncRangeModal
        open={syncRangeOpen}
        title={t('returnList.syncReturns')}
        defaultHours={48}
        presets={[
          { label: t('returnList.last24h'), hours: 24 },
          { label: t('returnList.last48h'), hours: 48 },
          { label: t('returnList.last7d'), hours: 24 * 7 },
          { label: t('returnList.last14d'), hours: 24 * 14 },
          { label: t('returnList.last30d'), hours: 24 * 30 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
      />

      <ProductListCard tabs={shopDimensionTabs} loading={Boolean(activeShopId) && loading} loadingLabel={t('returnList.querying')}>
        <div className="spo-drafts__table-wrap spo-ops-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--return">
            <colgroup>
              {/* 复选固定；产品信息约 24%；其余列（含操作）平分剩余宽度，避免固定 px 导致表头脱轨 */}
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              <col className="spo-ops-col spo-ops-col--absorb" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--ops spo-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('returnList.selectAll')}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('returnList.productInfo')}</th>
                <th scope="col">{t('returnList.returnNo')}</th>
                <th scope="col">{t('returnList.relatedOrder')}</th>
                <th scope="col">{t('returnList.reason')}</th>
                <th scope="col">{t('returnList.status')}</th>
                <th scope="col">{t('returnList.time')}</th>
                <th scope="col" className="spo-ops-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="spo-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index);
                  const no = pickField(row, 'returnOrderNo', 'returnNo');
                  const relatedOrderNo = pickField(row, 'orderNo', 'billNo', 'orderNumber');
                  const statusCode = pickField(row, 'returnOrderStatus', 'status');
                  const statusLabel = returnStatusText(statusCode);
                  const product = listProductSummary(row);
                  const returnReason = pickField(row, 'returnReason') || '—';
                  return (
                    <tr key={key}>
                      <td
                        className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: no || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--product">
                        {product.title || product.imageUrl || product.skc || product.merchantSku || product.refundText ? (
                          <div className="spo-return-product">
                            {product.imageUrl ? (
                              <SheinProductThumb
                                imageUrl={product.imageUrl}
                                className="spo-return-product__img"
                                placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                                alt={product.title || t('returnList.productAlt')}
                              />
                            ) : (
                              <div className="spo-return-product__img spo-return-product__img--empty" />
                            )}
                            <div className="spo-return-product__meta">
                              <div className="spo-return-product__title" title={product.title || undefined}>
                                {product.title || '—'}
                                {product.goodsCount > 1 ? (
                                  <span className="spo-return-product__count">{t('returnList.moreItems', { count: product.goodsCount })}</span>
                                ) : null}
                              </div>
                              <div className="spo-return-product__sku">
                                {t('returnList.skcPrefix')}{product.skc || '—'}
                              </div>
                              <div className="spo-return-product__sku">
                                {t('returnList.merchantSkuPrefix')}{product.merchantSku || '—'}
                              </div>
                              <div className="spo-return-product__refund">
                                {t('returnList.refundPrefix')}{product.refundText || '—'}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="spo-return-product__empty">{t('returnList.noProduct')}</span>
                        )}
                      </td>
                      <td className="spo-ops-td spo-ops-td--orderno">
                        <HoverCopyField
                          className="spo-return-copy"
                          value={no}
                          display={no ? <strong>{no}</strong> : '—'}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--related">
                        <HoverCopyField
                          className="spo-return-copy"
                          value={relatedOrderNo}
                          display={relatedOrderNo || '—'}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--reason" title={returnReason === '—' ? undefined : returnReason}>
                        {returnReason}
                      </td>
                      <td>
                        <span className={`spo-records__status spo-records__status--${statusModifier(statusCode)}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="spo-ops-td spo-ops-td--times">
                        <div className="spo-return-times">
                          <div>
                            <span className="spo-return-times__label">{t('returnList.applied')}</span>
                            <span>
                              {pickField(row, 'requestReturnTime', 'applyTime', 'createTime', 'returnTime') || '—'}
                            </span>
                          </div>
                          <div>
                            <span className="spo-return-times__label">{t('returnList.signed')}</span>
                            <span>{pickField(row, 'sellerSignedTime') || '—'}</span>
                          </div>
                          <div>
                            <span className="spo-return-times__label">{t('returnList.completed')}</span>
                            <span>{pickField(row, 'completedTime') || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="spo-ops-td spo-ops-td--ops spo-certs-td spo-certs-td--ops">
                        <div className="spo-drafts__actions">
                          <button
                            type="button"
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            disabled={!no}
                            onClick={() => openDetail(no)}
                          >
                            {t('returnList.detail')}
                          </button>
                          <button
                            type="button"
                            className="spo-btn spo-btn--primary spo-btn--sm"
                            disabled={!no || signingNo === no || batchSigning}
                            onClick={() => handleSign(no)}
                          >
                            {signingNo === no ? t('returnList.submitting') : t('returnList.confirmSign')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('returnList.pager')}
        />
      </ProductListCard>
    </>
  );
}
